/**
 * Model to encapsulate an API session
 *
 * It holds onto the token as well as refresh tokens.
 * It contains the logic for refreshing the token if it expires
 * It has the logic for retrieving and storing tokens in localStorage
 * Uh, other stuff
 *
 * This is a singleton model. There is only one per app.
 */
import _ from 'underscore';
import Backbone from 'backbone';
import Api from '@/lib/api/api';
import ApiPersistence from '@/lib/api/persistence';

var defaults = {
    access_token: '',
    expires_at: '',
    refresh_token: ''
};
var session = new Backbone.Model(defaults);
var funcs = {
    /**
     * Save the session info to localStorage (or similar)
     * We use the ApiPersistence model as an abstraction layer,
     * but it's really just using localStorage
     */
    save: function() {
        ApiPersistence.setTokenExpiry(this.get('expires_at'));
        ApiPersistence.setToken(this.get('access_token'));
        ApiPersistence.setTokenRefresh(this.get('refresh_token'));
    },
    /** 
     * Nullify, reset everything about the token. Do a reset then a
     * save before logging out.
     */
    reset: function() {
        this.set(defaults);
        ApiPersistence.clearToken();
        this.trigger('reset');
    },
    /**
     * Load the session from whatever persisence layer we're using.
     * In this case, it's ApiPersistence (localStorage).
     * Call load on the session first thing when the app loads.
     */
    load: function() {
        this.set({
            refresh_token: ApiPersistence.getTokenRefresh(),
            access_token: ApiPersistence.getToken(),
            expires_at: ApiPersistence.getTokenExpiry()
        });
    },
    /**
     * Refresh the access token using the existing refresh token.
     */
    refresh_token: function(options) {
        options = options || {};
        var data = { 
            token: this.get('refresh_token')
        };
        _.defaults(options, {
            data: JSON.stringify(data),
            url: Api.urls.auth_token(),
            headers: {
                    'Content-type': 'application/json'
            },
            method: 'POST'
        });
        return Api.ajax(options)
            .done(function(data){
                return this.set(data);
            }.bind(this))
            .done(this.save.bind(this))
        ;
    },

    /**
     * Attempt to login using the provided id and password.
     * Returns a jquery Deferred object
     */
    login: function(id, password) {
        var data = {
            id: id,
            password: password
        };
        return Api.ajax({
            type: 'POST',
            url: Api.urls.auth(),
            contentType: 'application/json',
            data: JSON.stringify(data),
        })
            .done(function(data){
                return this.set(data);
            }.bind(this))
            .done(this.save.bind(this))
        ;
    },
    /**
     * Attempt to login using totp code and MFA
     * @param totp String the code we get from authorizer app
     * @param login_token meta data from the last auth request for an MFA enabled account
     */
    mfa_login: function(totp, login_token) {
        var data = {
            login_token: login_token
        };

        return Api.ajax({
            type: 'POST',
            url: Api.urls.auth(),
            contentType: 'application/json',
            headers: {
                'Content-type': 'application/json',
                'x-electricimp-otp-token': totp
            },
            data: JSON.stringify(data),
        })
        .done(function (data) {
            return this.set(data);
        }.bind(this))
        .done(this.save.bind(this));
    },
    // hit /enrol and get planid and token
    getPlanAndTokenId: function() {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
        var payload = JSON.stringify({
            type: 'developer_token'
        });
        return Api.ajax({
            type: 'POST',
            headers: headers,
            url: Api.urls.enrol(),
            data: payload
        })
        .done(this.set.bind(this));
    },

     // hit /ui/enroll and get plan and token - new endpoint to make collaborator blinkup work
     getPlanAndTokenId_ui: function(account_id) {
        var headers = Api.getHeaderDefaults();
        headers['Content-Type'] = 'application/json';
         var payload = JSON.stringify({
             type: 'developer_token',
             owner_id: account_id
         });
        return Api.ajax({
            headers: headers,
            type: 'POST',
            url: Api.urls.enrol(),
            data: payload
        })
        .done(this.set.bind(this));
    },
    /**
     * Is the current access token expired?
     */
    is_valid: function() {
        var expires_at = new Date(this.get('expires_at'));
        return this.get('access_token') 
            && (expires_at && expires_at.getTime() > Date.now());
    },
    unset_cookie: function() {
        document.cookie.split(";").forEach(function (c) {
            document.cookie = 
                c.replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" 
                + new Date().toUTCString() + ";path=/"); 
        });
    }
};
// Bind all the functions here to the session itself 
// so that we can call them in any context.
Object.keys(funcs).forEach(function(funcname) {
    session[ funcname ] = funcs[ funcname ].bind(session);
});
export default session;

